import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { TrashIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { DeleteGalleryPayload, GalleryManagement } from '@/services/galleryManagement/galleryManagement.types';
import { Edit } from 'lucide-react';
import { AddGalleryImageForm } from '../../form/AddGalleryImageForm';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void> | void;
  onCancel: () => void;
}) => void;

export function getColumns(
  mutateAsync: (payload: DeleteGalleryPayload) => Promise<unknown>,
  openConfirmation: OpenConfirmationProps,
  onOpen: <T extends string>(config: {
    title: T;
    modalTitle: string | null;
    okText: React.ReactNode;
    component: React.ReactNode;
    cancelText?: string | React.ReactNode;
    size?: ModalSize;
    onCancel?: () => void;
  }) => void,
  t: (key: string, values?: Record<string, unknown>) => string,
): ColumnDef<GalleryManagement>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-primary truncate max-w-[10rem]">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'gallery',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.imageTitle')} />,
      cell: ({ row }) => {
        const gallery = row.original.gallery;
        const imgTitle = gallery?.description || '';
        return <div className="font-medium text-caption text-primary truncate max-w-[20rem]">{imgTitle}</div>;
      },

      meta: {
        label: 'Image Title',
      },
      enableHiding: false,
      enableSorting: false,
    },
    {
      accessorKey: 'gallery',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.image')} />,
      cell: ({ row }) => {
        const gallery = row.original.gallery;
        const imgAlt = gallery?.alt || '';

        const img = gallery?.path || '';
        return (
          <div>
            <img src={img} alt={imgAlt} className="h-15 aspect-[16 / 9]  rounded object-cover" />
          </div>
        );
      },
      meta: {
        label: 'Image',
      },
      enableHiding: false,
      enableSorting: false,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedOn')} />,
      cell: ({ row }) => {
        const date = Number(row.getValue('updatedDate'));
        return (
          <div className="font-medium text-caption text-primary whitespace-nowrap">
            {date
              ? formatDate(date, 'YYYY/MM/DD HH:mm:ss')
              : row.original.createdDate
                ? formatDate(Number(row.original.createdDate), 'YYYY/MM/DD HH:mm:ss')
                : '-'}
          </div>
        );
      },
      meta: {
        label: 'Updated Date',
      },
      enableHiding: false,
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.actions')} />,
      meta: {
        label: 'Action',
      },
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <Can rule={{ allOf: [PERMS.gallery.premium.update] }}>
            <Edit
              size={16}
              className="text-foreground-caption hover:text-foreground-detail"
              onClick={() => {
                onOpen({
                  modalTitle: 'storeManagement.galleryManagement.page.addGallery.manageClubImage',
                  title: 'Manage HostNameGallery',
                  okText: t('common.page.button.submit'),
                  size: 'lg',
                  component: (
                    <AddGalleryImageForm
                      clubId={row.original.clubId}
                      hostId={row.original.hostId}
                      data={row.original}
                      type="host"
                    />
                  ),
                });
              }}
            />
          </Can>
          <Can rule={{ allOf: [PERMS.gallery.premium.delete] }}>
            <TrashIcon
              size={16}
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirmation.title'),
                  description: t('storeManagement.basicStores.confirmationModal.deleteItem'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.confirm'),
                  onAction: async () => {
                    await mutateAsync({
                      galleryId: row.original.galleryId || '',
                      clubId: row.original.clubId || '',
                    });
                  },
                  onCancel: () => {},
                  size: 'lg',
                  variant: 'destructive',
                });
              }}
              className="cursor-pointer text-destructive-text hover:text-destructive-text ml-2"
            />
          </Can>
        </div>
      ),
      size: 40,
    },
  ];
}
